/**
 * Throttle any events.
 *
 * @author rafhun <rafhun@cubetech.ch>
 * @export
 * @param {string} type Event type to be throttled
 * @param {string} name Name of the new custom/throttled event
 * @param {null|HTMLElement} obj Object or DOMElement the event is attached to
 * @see https://css-tricks.com/debouncing-throttling-explained-examples/#requestanimationframe-raf
 * @example throttle('scroll', 'optimizedScroll'); window.addEventListener('optimizedScroll', callback);
 */
export function throttle(type, name, obj) {
    obj = obj || window;
    let running = false;
    const func = () => {
        if (running) {
            return;
        }

        running = true;

        requestAnimationFrame(() => {
            obj.dispatchEvent(new CustomEvent(name));
            running = false;
        });
    };
    obj.addEventListener(type, func);
}

/**
 * Simple function to check if an element has vertical or horizontal overflow.
 *
 * @author rafhun <rafhun@cubetech.ch>
 *
 * @export
 *
 * @param {HTMLElement} param0
 *
 * @returns {bool}
 */
export function isOverflown({ clientWidth, clientHeight, scrollWidth, scrollHeight }) {
    return scrollHeight > clientHeight || scrollWidth > clientWidth;
}

/**
 * Class based fading in of an element. Element needs to be hidden with the bootstrap `d-none` class. It is assumed that opacity is set to 0 using an inline style. The `fading` class is used to add a transition for opacity.
 *
 * @author rafhun <rafhun@cubetech.ch>
 *
 * @export
 *
 * @param {HTMLElement} element
 */
export function fadeIn(element) {
    if (!element.classList.contains('fading')) {
        element.classList.add('fading');
    }

    element.classList.remove('d-none');
    setTimeout(() => {
        element.style.opacity = null;
    }, 20);
}

/**
 * Class based fading out of an element. Element will first have its opacity dropped to 0, then it will be hidden using the `d-none` bootstrap class. The `fading` class is only responsible for the opacity transition.
 *
 * @author rafhun <rafhun@cubetech.ch>
 *
 * @export
 *
 * @param {HTMLElement} element
 */
export function fadeOut(element) {
    if (!element.classList.contains('fading')) {
        element.classList.add('fading');
    }
    element.style.opacity = 0;

    element.addEventListener(
        'transitionend',
        () => {
            element.classList.add('d-none');
        },
        {
            capture: false,
            once: true,
            passive: false,
        }
    );
}

/**
 * Compute the scrollbar width and assign that value to a custom CSS property.
 *
 * @author rafhun <rafhun@cubetech.ch>
 * @export
 */
export function defineScrollbarWidth() {
    document.documentElement.style.setProperty(
        '--scrollbar-width',
        `${window.visualViewport.width - document.body.offsetWidth}px`
    );
}

export function setHeaderHeight() {
    let headerHeight = 0;
    const header = document.querySelector('.main-container > header');

    if (header) {
        headerHeight = header.offsetHeight;
    }

    document.documentElement.style.setProperty('--ct-header-height', `${headerHeight}px`);
}
